
import { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search } from "lucide-react";
import { useOrganization } from "@/context/OrganizationContext";

import { Diagram, SortColumn, SortDirection, fetchDiagrams, createDiagram, updateDiagram, deleteDiagram } from "@/utils/diagramService";
import { DiagramTable } from "@/components/diagrams/DiagramTable";
import { EditDiagramDialog } from "@/components/diagrams/EditDiagramDialog";
import { CreateDiagramDialog } from "@/components/diagrams/CreateDiagramDialog";
import { DeleteDiagramDialog } from "@/components/diagrams/DeleteDiagramDialog";

export default function Diagramas() {
  const { currentOrganization } = useOrganization();
  const [diagrams, setDiagrams] = useState<Diagram[]>([]);
  const [loading, setLoading] = useState(true);
  const [nameFilter, setNameFilter] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentDiagram, setCurrentDiagram] = useState<Diagram | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [diagramToDelete, setDiagramToDelete] = useState<Diagram | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (currentOrganization?.id) {
      loadDiagrams();
    }
  }, [currentOrganization, sortColumn, sortDirection]);

  const loadDiagrams = async () => {
    setLoading(true);
    try {
      if (currentOrganization?.id) {
        const data = await fetchDiagrams(currentOrganization?.id, sortColumn, sortDirection);
        setDiagrams(data);
      }
    } catch (error) {
      console.error("Erro ao carregar diagramas:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleEdit = (diagram: Diagram) => {
    setCurrentDiagram(diagram);
    setEditDialogOpen(true);
  };

  const handleDelete = (diagram: Diagram) => {
    setDiagramToDelete(diagram);
    setDeleteDialogOpen(true);
  };

  const handleUpdateDiagram = async (name: string) => {
    if (!currentDiagram) return;
    
    setIsSaving(true);
    try {
      const success = await updateDiagram(currentDiagram.id, name);
      
      if (success) {
        await loadDiagrams();
        setEditDialogOpen(false);
      }
    } catch (error) {
      console.error("Erro ao atualizar diagrama:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateDiagram = async (name: string) => {
    if (!currentOrganization?.id) return;
    
    setIsSaving(true);
    try {
      const success = await createDiagram(name, currentOrganization.id);
      
      if (success) {
        await loadDiagrams();
        setCreateDialogOpen(false);
      }
    } catch (error) {
      console.error("Erro ao criar diagrama:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!diagramToDelete) return;
    
    setIsDeleting(true);
    try {
      const success = await deleteDiagram(diagramToDelete.id);
      
      if (success) {
        await loadDiagrams();
        setDeleteDialogOpen(false);
      }
    } catch (error) {
      console.error("Erro ao excluir diagrama:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredDiagrams = useMemo(() => {
    return diagrams.filter(diagram =>
      diagram.name.toLowerCase().includes(nameFilter.toLowerCase())
    );
  }, [diagrams, nameFilter]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Diagramas</h1>
          <p className="text-muted-foreground">
            Visualize e gerencie seus diagramas de arquitetura.
          </p>
        </div>
        <Button 
          onClick={() => setCreateDialogOpen(true)}
          className="bg-primary hover:bg-primary/90"
        >
          <Plus className="mr-2 h-4 w-4" /> Novo Diagrama
        </Button>
      </div>

      <div className="flex items-center space-x-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Filtrar por nome do diagrama..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      
      <Card className="border border-border/40 shadow-sm">
        <CardContent className="p-0">
          <DiagramTable 
            diagrams={filteredDiagrams}
            loading={loading}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      <EditDiagramDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        diagram={currentDiagram}
        onSave={handleUpdateDiagram}
      />

      <CreateDiagramDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSave={handleCreateDiagram}
      />

      <DeleteDiagramDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        diagram={diagramToDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
